import { useCallback, useState } from 'react';
import { noop } from '@cbhq/cds-utils';
import { Button } from '@cbhq/cds-web/buttons';
import { ListCell } from '@cbhq/cds-web/cells';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@cbhq/cds-web/overlays';
import { TextBody } from '@cbhq/cds-web/typography';

import { CoinbaseSmartContract } from ':cloud/types/ts_types';
import { truncateMiddle } from ':cloud/utils/common';
import { ClipboardIcon } from ':cloud/widgets/sharedcomponents';
import { CoinbaseSmartContractEditFieldsModal } from ':cloud/widgets/smartContracts/CoinbaseSmartContractEditFieldsModal';

import { getNetwork } from '../helpers';

type SmartContractEditModalProps = {
  onRequestClose?: () => void;
  onSubmit: (smartContract: CoinbaseSmartContract) => void;
  onDelete: (smartContract: CoinbaseSmartContract) => void;
  smartContract: CoinbaseSmartContract;
};

export function CoinbaseSmartContractEditModal({
  onRequestClose,
  onSubmit,
  onDelete,
  smartContract,
}: Readonly<SmartContractEditModalProps>) {
  const [isEditFieldsModalVisible, setIsEditFieldsModalVisible] = useState(false);
  const { isPhone } = useBreakpoints();

  const handleEditButtonPress = useCallback(() => {
    setIsEditFieldsModalVisible(true);
  }, []);

  const handleEditFieldsModalClose = useCallback(() => {
    setIsEditFieldsModalVisible(false);
  }, []);

  const handleEditFieldsSubmit = useCallback(
    (updatedSmartContract: CoinbaseSmartContract) => {
      onSubmit(updatedSmartContract);
    },
    [onSubmit],
  );

  // TODO: Delete is not implemented on API
  // https://jira.coinbase-corp.com/browse/CDF-4027
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDelete = useCallback(() => {
    onDelete(smartContract);
    onRequestClose?.();
  }, [onDelete, smartContract, onRequestClose]);

  return (
    <Modal onRequestClose={onRequestClose || noop} visible width={isPhone ? undefined : 480}>
      {isEditFieldsModalVisible ? (
        <CoinbaseSmartContractEditFieldsModal
          smartContract={smartContract}
          onRequestClose={handleEditFieldsModalClose}
          onSubmit={handleEditFieldsSubmit}
        />
      ) : (
        <>
          <ModalHeader title="Smart Contract Details" />
          <ModalBody>
            <VStack gap={2}>
              <ListCell description="Contract name" detail={smartContract.contractName} />
              <ListCell
                description="Contract address"
                action={
                  <HStack alignItems="center">
                    <VStack background="primaryWash" spacingHorizontal={1}>
                      <TextBody as="p">
                        {truncateMiddle(smartContract.contractAddress, 9, 8, '...')}
                      </TextBody>
                    </VStack>
                    <ClipboardIcon
                      text={smartContract.contractAddress}
                      iconSize="s"
                      stopPropagation
                    />
                  </HStack>
                }
              />
              <ListCell description="Network" detail={getNetwork(smartContract.networkId)} />
              <ListCell
                title="ABI"
                description={JSON.stringify(JSON.parse(smartContract.abi), null, 2)}
                multiline
              />
            </VStack>
          </ModalBody>
          <ModalFooter
            primaryAction={
              <Button variant="secondary" onPress={handleEditButtonPress}>
                Edit
              </Button>
            }
            // TODO: Delete is not implemented on API
            // https://jira.coinbase-corp.com/browse/CDF-4027
            // primaryAction={
            //   <Button variant="negative" onPress={handleDelete}>
            //     Delete
            //   </Button>
            // }
            // secondaryAction={
            //   <Button variant="secondary" onPress={handleEditButtonPress}>
            //     Edit
            //   </Button>
            // }
          />
        </>
      )}
    </Modal>
  );
}
